import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Shield, TrendingUp, Users, Mail, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/SEOHead';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AboutPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'about_page' }),
      });
      toast.success('Message sent. We will be in touch shortly.');
      setForm({ name: '', email: '', company: '', message: '' });
    } catch {
      toast.error('Something went wrong. Please email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="about-page">
      <SEOHead
        title="About Nuvanta Solutions and TradePath | Australian Supply Chain Intelligence"
        description="Nuvanta Solutions builds technology for APAC trade. TradePath is our supply chain risk intelligence platform for Australian importers. Learn our story."
      />

      {/* Nav */}
      <header>
        <nav className="bg-[#0A2533] border-b border-white/10" aria-label="About page navigation">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-white font-bold text-lg">Nuvanta Solutions</span>
              <span className="text-[#4DD0E1] text-lg">|</span>
              <span className="text-[#4DD0E1] font-semibold text-lg">TradePath</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/pricing" className="text-white/80 hover:text-white text-sm transition-colors hidden sm:inline">Pricing</Link>
              <Link to="/insights" className="text-white/80 hover:text-white text-sm transition-colors hidden sm:inline">Insights</Link>
              <Link to="/signin" className="text-white/80 hover:text-white text-sm transition-colors">Sign in</Link>
              <Link to="/register" className="bg-[#00707C] hover:bg-[#005F6A] text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors">
                Start your trial
              </Link>
            </div>
          </div>
        </nav>
      </header>

      <main id="main-content" className="max-w-5xl mx-auto px-6 py-12">

        {/* Hero */}
        <section className="text-center mb-16" data-testid="about-hero">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D3B4F] tracking-tight mb-4" data-testid="about-h1">
            About Nuvanta Solutions
          </h1>
          <p className="text-[#0097A7] text-lg sm:text-xl max-w-3xl mx-auto">
            An Australian technology and advisory group building tools for APAC trade.
          </p>
          <p className="text-[#4E6974] text-sm mt-2 italic">Where Innovation Meets Execution</p>
        </section>

        {/* Our lineage */}
        <section className="bg-white rounded-xl p-8 sm:p-12 mb-8 shadow-sm" data-testid="about-lineage">
          <h2 className="text-xl font-bold text-[#0D3B4F] mb-6">Our lineage</h2>
          <div className="space-y-4 text-[#4E6974] leading-relaxed">
            <p>
              Nuvanta Solutions was founded to address a gap we kept seeing in Australian mid-market business: organisations with AUD 10 million to 500 million in annual revenue that source globally, but lack the intelligence tools that large multinationals take for granted. These businesses navigate complex APAC supply chains with spreadsheets, outdated government reports, and gut instinct. We believed they deserved better.
            </p>
            <p>
              Our team brings together experience across trade advisory, government procurement, technology, and data analytics. We have worked with Australian government agencies, defence supply chain programmes, and private sector importers who face the same fundamental challenge: understanding what is happening in their supply chain before it becomes a crisis. That experience shaped everything about how we built TradePath.
            </p>
            <p>
              TradePath is the first product in the Nuvanta portfolio. It was designed from the ground up for Australian conditions: Australian English, AUD-denominated analysis, DFAT-aligned country risk frameworks, and a focus on the APAC trading region where most Australian supply chains are concentrated. We monitor 42 countries across Asia-Pacific, Europe, the Americas, and the Middle East, covering the sourcing origins that matter most to Australian importers.
            </p>
          </div>
        </section>

        {/* What we believe */}
        <section className="mb-8" data-testid="about-beliefs">
          <h2 className="text-xl font-bold text-[#0D3B4F] mb-6">What we believe</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Globe, title: "Trade creates prosperity", body: "International trade is the engine of Australian economic growth. Our role is to help businesses trade with confidence, not to discourage engagement with complex markets. Better intelligence leads to better outcomes." },
              { icon: Shield, title: "Risk is manageable with the right data", body: "Supply chain disruptions are not random acts of fate. Most are foreseeable with the right monitoring. We believe that Australian businesses of any size deserve access to the kind of risk intelligence that was previously available only to large multinationals." },
              { icon: TrendingUp, title: "Australian businesses can lead", body: "Australian importers and procurement teams are among the most sophisticated in the world. What they lack is not capability, but tools that match their ambition. TradePath exists to close that gap." },
            ].map((belief, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-[#0097A7]/10 flex items-center justify-center mb-4">
                  <belief.icon className="h-5 w-5 text-[#0097A7]" />
                </div>
                <h3 className="font-bold text-[#0D3B4F] mb-2">{belief.title}</h3>
                <p className="text-sm text-[#4E6974] leading-relaxed">{belief.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* The team */}
        <section className="bg-white rounded-xl p-8 sm:p-12 mb-8 shadow-sm" data-testid="about-team">
          <h2 className="text-xl font-bold text-[#0D3B4F] mb-4">The team</h2>
          <p className="text-[#4E6974] leading-relaxed mb-6">
            Our team is based in Melbourne and Brisbane, with experience spanning trade advisory, government procurement, defence supply chains, and data analytics. Full team profiles are coming soon.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-[#F5F5F5] rounded-lg p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-[#0D3B4F]/10 mx-auto mb-3 flex items-center justify-center">
                  <Users className="h-6 w-6 text-[#4E6974]" />
                </div>
                <div className="text-sm text-[#4E6974]">Team member</div>
                <div className="text-xs text-[#4E6974]/60">Profile coming soon</div>
              </div>
            ))}
          </div>
        </section>

        {/* Products */}
        <section className="mb-8" data-testid="about-products">
          <h2 className="text-xl font-bold text-[#0D3B4F] mb-6">Our products</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-[#0097A7]">
              <h3 className="font-bold text-[#0D3B4F] mb-2">TradePath</h3>
              <p className="text-sm text-[#4E6974] leading-relaxed mb-4">
                Supply chain risk intelligence for Australian businesses. Monitor 42 countries, track supplier risk, receive real-time alerts, and generate deep dive country reports. Built for importers, government procurement teams, and defence supply chain professionals.
              </p>
              <Link to="/pricing" className="text-[#0097A7] hover:underline text-sm font-medium inline-flex items-center gap-1">
                View pricing <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="bg-[#F5F5F5] rounded-xl p-6 border-l-4 border-[#E0E0E0]">
              <h3 className="font-bold text-[#4E6974] mb-2">More products coming</h3>
              <p className="text-sm text-[#4E6974]/70 leading-relaxed">
                Nuvanta Solutions is building a suite of intelligence tools for APAC trade. Stay tuned for announcements.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-white rounded-xl p-8 sm:p-12 mb-8 shadow-sm" data-testid="about-contact">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-xl font-bold text-[#0D3B4F] mb-4">Get in touch</h2>
              <p className="text-[#4E6974] leading-relaxed mb-4">
                For pre-sales enquiries, partnerships, or general questions, contact us directly or use the form.
              </p>
              <div className="flex items-center gap-2 text-[#0097A7] font-medium">
                <Mail className="h-4 w-4" />
                <a href="mailto:rj.bakelaar@nuvantasolutions.com.au" className="hover:underline">
                  rj.bakelaar@nuvantasolutions.com.au
                </a>
              </div>
              <p className="text-xs text-[#4E6974] mt-6">
                Nuvanta Solutions Pty Ltd<br />
                Melbourne | Brisbane<br />
                <a href="https://nuvantasolutions.com.au" className="text-[#0097A7] hover:underline">nuvantasolutions.com.au</a>
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input type="text" placeholder="Your name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none" data-testid="contact-name" />
              <input type="email" placeholder="Work email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none" data-testid="contact-email" />
              <input type="text" placeholder="Organisation" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none" data-testid="contact-company" />
              <textarea placeholder="How can we help?" rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none resize-none" data-testid="contact-message" />
              <Button type="submit" disabled={submitting} className="w-full bg-[#00707C] hover:bg-[#005F6A] text-white" data-testid="contact-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {submitting ? 'Sending...' : 'Send message'}
              </Button>
            </form>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-[#0D3B4F] rounded-xl p-8 sm:p-12 text-center" data-testid="about-cta">
          <h2 className="text-2xl font-bold text-white mb-3">Start your free trial</h2>
          <p className="text-white/70 mb-6 max-w-xl mx-auto">
            See what TradePath intelligence looks like for your supply chain. From AUD 2,500 per month.
          </p>
          <Link to="/register">
            <Button className="bg-[#00707C] hover:bg-[#005F6A] text-white px-8 py-3 rounded-lg font-medium" data-testid="about-trial-btn">
              Start your free trial
            </Button>
          </Link>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-[#0A2533] mt-12">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-[#4DD0E1] text-sm font-semibold mb-1">
            A <a href="https://nuvantasolutions.com.au" className="underline hover:text-white">Nuvanta Solutions</a> product
          </p>
          <p className="text-white/40 text-xs">&copy; 2026 Nuvanta Solutions Pty Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
